import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import * as _ from 'lodash-es';
import moment from 'moment';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EntityChartMapService } from './entity-chart-map.service';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

const ENTITY_EDIT_DATE_FIELDS: ReadonlySet<string> = new Set([
  'DATE_INCORPORATED',
  'ACQUISITION_DATE',
  'DATE_REINCORPORATED',
  'DATE_DISSOLVED',
  'DATE_LIQUIDATED',
  'DATE_SPAWNED_CONSUMED',
  'DATE_SOLD',
  'TAX_YEAR_BEGIN',
  'TAX_YEAR_END',
]);

const ENTITY_EDIT_DATE_FORMAT = 'YYYY-MM-DDThh:mm:ss.sssZ';

const ENTITY_EDIT_FIELD_LABELS: Readonly<Record<string, string>> = {
  REPORTING_PERIOD: 'Reporting Period',
  LE_NAME: 'Legal Entity Name',
  DATE_INCORPORATED: 'Date Incorporated',
  ACQUISITION_DATE: 'Acquisition Date',
  DATE_REINCORPORATED: 'Date Reincorporated',
  DATE_DISSOLVED: 'Date Dissolved',
  DATE_LIQUIDATED: 'Date Liquidated',
  DATE_SPAWNED_CONSUMED: 'Date Spawned Consumed',
  DATE_SOLD: 'Date Sold',
  CONSUMED_BY: 'Consumed By',
  MAINTENANCE_OWNER: 'Maintenance Owner',
  COUNTRY: 'Country Incorporated',
  GEIS_CODE: 'GEIS Code',
  FUNCTIONAL_CURRENCY: 'Functional Currency',
  REFRESH_Y_N: 'Refresh Y/N',
  REASON: 'Reason',
  TAX_TYPE: 'Tax Type',
  PREPARE_Y_N: 'Prepare Y/N',
  ENTITY_CLASSIFICATION: 'Entity Classification',
  FSE_Y_N: 'FSE Y/N',
  TAX_YEAR_BEGIN: 'Tax Year Begin',
  TAX_YEAR_END: 'Tax Year End',
  NOV_YEAR_END: 'Nov Year End',
};

@Component({
    selector: 'gtw-entity-chart-map',
    templateUrl: './entity-chart-map.component.html',
    styleUrls: ['./entity-chart-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityChartMapComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  rowData: any = {};
  gridDataInput: any[] = [];
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  modalTitle = 'Entity Chart Map';
  templateMode = 'chartmap';
  activeTab: 'map' | 'update' = 'map';

  entityTypeChange = 'taxCode';
  gridDataList: any[] = [];
  meCodeData: any[] = [];
  chartData: any[] = [];
  currData: any[] = [];
  existingChartData: any[] = [];
  medisplay: string[] = [];
  currdisplay: string[] = [];
  dcsChart: any[] = [];
  firChart: any[] = [];
  noRecords = false;
  entityLookUpinit = false;
  entitylookUpMapping = true;

  updateRows: any[] = [];
  updateDraft: any = {};
  updateFields: string[] = [
    'REPORTING_PERIOD',
    'LE_NAME',
    'DATE_INCORPORATED',
    'ACQUISITION_DATE',
    'DATE_REINCORPORATED',
    'DATE_DISSOLVED',
    'DATE_LIQUIDATED',
    'DATE_SPAWNED_CONSUMED',
    'DATE_SOLD',
    'CONSUMED_BY',
    'MAINTENANCE_OWNER',
    'COUNTRY',
    'GEIS_CODE',
    'FUNCTIONAL_CURRENCY',
    'REFRESH_Y_N',
    'REASON',
    'TAX_TYPE',
    'PREPARE_Y_N',
    'ENTITY_CLASSIFICATION',
    'FSE_Y_N',
    'TAX_YEAR_BEGIN',
    'TAX_YEAR_END',
    'NOV_YEAR_END',
  ];

  private isInitialized = false;
  private isLoading = false;

  @Input('template-mode')
  set _template_mode(v: string) {
    if (v) {
      this.templateMode = v;
    }
  }

  @Input('modal-desc')
  set _modal_desc(v: string) {
    if (v) {
      this.modalTitle = v;
    }
  }

  @Input('base-urls')
  set _base_urls(v: string) {
    const u = typeof v === 'string'
      ? safeJsonInput<any>(v, null, 'entity-chart-map.base-urls')
      : v;
    if (u && Object.keys(u).length > 0) {
      this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, ...u };
      this.tryInit();
    }
  }

  @Input('header-data')
  set _header_data(v: string) {
    const h = typeof v === 'string'
      ? safeJsonInput<any>(v, null, 'entity-chart-map.header-data')
      : v;
    if (h && Object.keys(h).length > 0) {
      this.globalService = h.globalParams || {};
      this.USER_SETTINGS = h.userSettingsData || {};
      this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, ...(h.generalConfigData || {}) };
      this.tryInit();
    }
  }

  @Input('row-data')
  set _row_data(v: string) {
    const d = typeof v === 'string'
      ? safeJsonInput<any>(v, {}, 'entity-chart-map.row-data')
      : v;
    this.rowData = d || {};
    this.tryInit();
  }

  @Input('grid-data')
  set _grid_data(v: string) {
    const d = typeof v === 'string'
      ? safeJsonInput<any>(v, [], 'entity-chart-map.grid-data')
      : v;
    this.gridDataInput = Array.isArray(d) ? d : [];
    this.tryInit();
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    const p = typeof v === 'string'
      ? safeJsonInput<any>(v, null, 'entity-chart-map.filter-params')
      : v;
    if (p && Object.keys(p).length > 0) {
      this.filterParams = p;
      this.tryInit();
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private entityChartMapService: EntityChartMapService,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    const hasRowData = !!this.rowData && Object.keys(this.rowData).length > 0;
    const hasGridData = Array.isArray(this.gridDataInput) && this.gridDataInput.length > 0;
    if (!hasRowData && !hasGridData) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.prepareGridData();
    this.prepareUpdateRows();
    if (this.templateMode === 'entity-edit') {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    this.loadFormData();
  }

  private prepareGridData(): void {
    const source = this.resolveRowSource();
    const prepared = source.map((item: any) => {
      const data = _.cloneDeep(item?.data || item || {});
      data.CURR_EDIT = data.SOURCE_SYSTEM_FLAG === 'D' ? 'USD' : '';
      data.IS_DISABLE = false;
      data.LEDGER_EDIT = 'DEFAULT';
      data.TaxCODE_EDIT = '';
      data.ME_EDIT = '';
      data.CHARTCODE_EDIT = '';
      data.taxCode_Error = false;
      data.ledger_Error = false;
      data.me_Error = false;
      data.curr_Error = false;
      data.chart_Error = false;
      return data;
    });
    this.gridDataList = _.uniqBy(prepared, (e) => e.LE_KEY);
  }

  private prepareUpdateRows(): void {
    const source = this.resolveRowSource();
    this.updateRows = _.cloneDeep(source.map((item: any) => item?.data || item || {}));
  }

  /**
   * Resolve the array of rows to operate on. Entry points differ between
   * modes: `chartmap` receives multi-row selection via `gridDataInput`, while
   * `entity-edit` (LEID pencil) receives a single flat row via `rowData`.
   * Mirrors the AngularJS EntityEditController fallback `gridData || rowData`.
   */
  private resolveRowSource(): any[] {
    if (Array.isArray(this.gridDataInput) && this.gridDataInput.length > 0) {
      return this.gridDataInput;
    }
    if (Array.isArray(this.rowData)) {
      return this.rowData;
    }
    if (this.rowData && typeof this.rowData === 'object' && Object.keys(this.rowData).length > 0) {
      return [this.rowData];
    }
    return [];
  }

  private loadFormData(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const taxYear = this.rowData.TAX_YEAR || this.filterParams.tax_year || this.globalService.tax_year;
    const scenario = this.rowData.SCENARIO || this.filterParams.scenario || this.globalService.scenario;
    const jcdKey = this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService.jcd_key;

    Promise.all([
      firstValueFrom(this.entityChartMapService.loadJsonObject(baseUrl, { action_code: 'pk25zj', tax_year: taxYear, scenario, jcd_key: jcdKey })),
      firstValueFrom(this.entityChartMapService.loadJsonObject(baseUrl, { action_code: '096e9s', tax_year: taxYear, scenario, jcd_key: jcdKey })),
      firstValueFrom(this.entityChartMapService.loadJsonObject(baseUrl, { action_code: 'gug95n', tax_year: taxYear, scenario, jcd_key: jcdKey })),
      firstValueFrom(this.entityChartMapService.loadJsonObject(baseUrl, {
        action_code: 'wvhg22',
        tax_year: taxYear,
        scenario,
        jcd_key: jcdKey,
        le_key: this.gridDataList.map((item) => item.LE_KEY).join(','),
      })),
    ]).then(
      (responses: any[]) => {
        this.meCodeData = responses[0]?.jsonObject || [];
        this.chartData = responses[1]?.jsonObject || [];
        this.currData = responses[2]?.jsonObject || [];
        this.existingChartData = responses[3]?.jsonObject || [];

        this.medisplay = _.uniq(this.meCodeData.map((r: any) => r.ME_CODE));
        this.currdisplay = _.uniq(this.currData.map((r: any) => r.FROM_CURRENCY));
        this.dcsChart = this.chartData.filter((r: any) => r.SOURCE_SYSTEM === 'DCS');
        this.firChart = this.chartData.filter((r: any) => r.SOURCE_SYSTEM === 'FIR');
        this.noRecords = this.existingChartData.length === 0;
        this.entityLookUpinit = this.existingChartData.length > 0;
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.showFormLoader = false;
        this.gtwSnackbarService.openSnackBar('Error loading Entity Chart Map data', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  onEntityTypeChange(): void {
    this.entityTypeChange = String(this.entityTypeChange);
    this.reset();
  }

  reset(): void {
    this.gridDataList = this.gridDataList.map((row) => {
      row.CURR_EDIT = row.SOURCE_SYSTEM_FLAG === 'D' ? 'USD' : '';
      row.LEDGER_EDIT = 'DEFAULT';
      row.TaxCODE_EDIT = '';
      row.ME_EDIT = '';
      row.CHARTCODE_EDIT = '';
      row.taxCode_Error = false;
      row.ledger_Error = false;
      row.me_Error = false;
      row.curr_Error = false;
      row.chart_Error = false;
      return row;
    });
    this.cdr.detectChanges();
  }

  validateChangedFields(index: number, type: string): void {
    const row = this.gridDataList[index];
    if (!row) {
      return;
    }
    if (type === 'LEDGER') {
      row.ledger_Error = !row.LEDGER_EDIT;
    } else if (type === 'ME_CODE') {
      row.me_Error = !row.ME_EDIT;
    } else if (type === 'CURR') {
      row.curr_Error = !row.CURR_EDIT;
    } else if (type === 'CHART') {
      row.chart_Error = !row.CHARTCODE_EDIT;
    } else if (type === 'TAX_CODE') {
      row.taxCode_Error = !row.TaxCODE_EDIT;
    }
  }

  private validateRequiredFields(): number {
    let hasError = 0;
    this.gridDataList.forEach((row) => {
      row.ledger_Error = !row.LEDGER_EDIT;
      row.me_Error = !row.ME_EDIT;
      row.curr_Error = !row.CURR_EDIT;
      row.chart_Error = !row.CHARTCODE_EDIT;
      if (this.entityTypeChange === 'taxCode') {
        row.taxCode_Error = !row.TaxCODE_EDIT;
      }
      if (row.ledger_Error || row.me_Error || row.curr_Error || row.chart_Error || row.taxCode_Error) {
        hasError = 1;
      }
    });
    return hasError;
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', '');
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    if (this.validateRequiredFields() === 1) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    const tyFallback = this.rowData?.TAX_YEAR ?? this.filterParams?.tax_year ?? this.globalService?.tax_year;
    const scFallback = this.rowData?.SCENARIO ?? this.filterParams?.scenario ?? this.globalService?.scenario;
    const jcdFallback = this.rowData?.JCD_KEY ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key;
    const sendObj = this.gridDataList.map((row: any) => {
      const meInfo = this.meCodeData.find((m: any) => m.ME_CODE === row.ME_EDIT) || {};
      const chartInfo = this.chartData.find((c: any) => c.CHART_CODE === row.CHARTCODE_EDIT) || {};
      return {
        TYPE: 'I',
        TAX_YEAR: row.TAX_YEAR ?? tyFallback,
        SCENARIO: row.SCENARIO ?? scFallback,
        JCD_KEY: row.JCD_KEY ?? jcdFallback,
        LE_KEY: row.LE_KEY,
        COMBINATION_KEY: row.COMBINATION_KEY,
        LEID: row.LEID,
        REPORTING_PERIOD: row.REPORTING_PERIOD,
        TAXCODE_NAME: 'Tax Code Name',
        ME_KEY: meInfo.ME_KEY,
        ME_CODE: meInfo.ME || row.ME_EDIT,
        CHART_KEY: chartInfo.CHART_KEY,
        TAX_CODE: this.entityTypeChange === 'taxCode' ? row.TaxCODE_EDIT : row.CDR_NO,
        CURR: row.CURR_EDIT,
        LEDGER: row.LEDGER_EDIT,
      };
    });
    const returnObj = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      screen_key: this.globalService?.screen_key || '',
      save_type: this.entityTypeChange,
    };

    this.entityChartMapService
      .saveEntityChartMap(
        this.GENERAL_CONFIG.base_url,
        returnObj,
        sendObj,
        { globalParams: this.globalService },
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (result?.errorMessage && result.errorMessage !== 'null') {
            const keys = String(result.errorMessage).split('\n').pop()?.split(', ') || [];
            this.gridDataList.forEach((row) => {
              if (keys.includes(String(row.LE_KEY))) {
                row.taxCode_Error = true;
                row.me_Error = true;
              }
            });
            this.gtwSnackbarService.openSnackBar('Please correct the highlighted Company Codes,they alreday exist', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Entity Chart Map has been successfully save', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  applyValues(field: string, value: any): void {
    this.updateRows.forEach((row) => {
      row[field] = value;
    });
  }

  isDateField(field: string): boolean {
    return ENTITY_EDIT_DATE_FIELDS.has(field);
  }

  getLabel(field: string): string {
    return ENTITY_EDIT_FIELD_LABELS[field] || field;
  }

  toDateInputValue(v: any): string {
    if (!v) return '';
    const m = moment(v);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }

  onDateInput(target: any, field: string, event: Event): void {
    const raw = (event.target as HTMLInputElement)?.value;
    target[field] = raw ? moment(raw).format(ENTITY_EDIT_DATE_FORMAT) : '';
    this.cdr.markForCheck();
  }

  saveEntityUpdates(): void {
    if (this.crudLoading) {
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    const payload = this.updateRows.map((row) => {
      const out = { ...row };
      this.updateFields.forEach((key) => {
        if (ENTITY_EDIT_DATE_FIELDS.has(key)) {
          const value = out[key];
          out[key] = value ? moment(value).format(ENTITY_EDIT_DATE_FORMAT) : '';
        } else if (!out[key]) {
          out[key] = '';
        }
      });
      return out;
    });

    this.entityChartMapService
      .updateEntityChartDetails(this.GENERAL_CONFIG.base_url, payload, this.USER_SETTINGS, this.rowData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar('An error has occurred. If this error continues please go to the help page and contact support', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  selectTab(tab: 'map' | 'update'): void {
    this.activeTab = tab;
    this.cdr.detectChanges();
  }
}
